import { BoundingSphere, Color, ColorGeometryInstanceAttribute, ComponentDatatype, Geometry, GeometryAttribute, GeometryInstance, GeometryPipeline, Matrix4, PerInstanceColorAppearance, Primitive, PrimitiveType, VertexFormat, } from "cesium";
/**
 * Lift each face along its normal to avoid z-fighting with the underlying
 * mesh (e.g. FlatGround / TemporaryRoad reference plane).
 *
 * Why: the face-selection primitive is built from the same vertices as the
 * underlying mesh, so both occupy identical depth. When the underlying mesh
 * is fully opaque (opacity = 100%), it writes to the depth buffer first and
 * wins the pick pass → scene.pick returns the underlying mesh, not the face
 * primitive, and hover/click silently fail. Lowering the underlying mesh
 * opacity "accidentally" fixed it because translucent primitives skip depth
 * writes.
 *
 * Fix: offset each face vertex by its face normal so the face primitive
 * physically sits slightly in front of the mesh, making picking independent
 * of the underlying mesh opacity. 2cm is imperceptible at typical viewing
 * scales but large enough to beat float32 depth precision.
 */
const FACE_LIFT_METERS = 0.02;
const COLOR_DEFAULT = ColorGeometryInstanceAttribute.fromColor(Color.fromBytes(255, 255, 255, 38));
const COLOR_SELECTED = ColorGeometryInstanceAttribute.fromColor(Color.fromBytes(255, 167, 38, 153));
const COLOR_SEED = ColorGeometryInstanceAttribute.fromColor(Color.fromBytes(255, 87, 34, 179));
const COLOR_HOVERED = ColorGeometryInstanceAttribute.fromColor(Color.fromBytes(255, 235, 59, 128));
/**
 * Alpha must stay above zero. Cesium's pick fragment shader discards
 * fragments whose alpha is 0 (`createPickFragmentShaderSource`), so a fully
 * transparent excluded face becomes unpickable and can never be clicked back.
 */
const COLOR_EXCLUDED = ColorGeometryInstanceAttribute.fromColor(Color.fromBytes(0, 0, 0, 64));
/**
 * Create a single Cesium Primitive with one GeometryInstance per triangle face.
 * Each instance has an id = { faceIndex } for Cesium scene.pick.
 * Geometry must be in WGS84 (ECEF) coordinates.
 *
 * Performance note: one GeometryInstance per face is required for per-face
 * picking via scene.pick (Cesium identifies instances by id). For meshes
 * with >10K faces this becomes memory-heavy. If scaling is needed, consider
 * a single-geometry approach with manual triangle-index raycast instead of
 * Cesium's built-in picking.
 */
export function createPerFacePrimitive(geometryWgs84) {
    const { vertices, indices } = geometryWgs84;
    const faceCount = indices.length / 3;
    const instances = new Array(faceCount);
    const faceIds = new Array(faceCount);
    for (let f = 0; f < faceCount; f++) {
        const i0 = indices[f * 3];
        const i1 = indices[f * 3 + 1];
        const i2 = indices[f * 3 + 2];
        const v0x = vertices[i0 * 3];
        const v0y = vertices[i0 * 3 + 1];
        const v0z = vertices[i0 * 3 + 2];
        const v1x = vertices[i1 * 3];
        const v1y = vertices[i1 * 3 + 1];
        const v1z = vertices[i1 * 3 + 2];
        const v2x = vertices[i2 * 3];
        const v2y = vertices[i2 * 3 + 1];
        const v2z = vertices[i2 * 3 + 2];
        // Face normal in ECEF via cross product of edges
        const e1x = v1x - v0x;
        const e1y = v1y - v0y;
        const e1z = v1z - v0z;
        const e2x = v2x - v0x;
        const e2y = v2y - v0y;
        const e2z = v2z - v0z;
        let nx = e1y * e2z - e1z * e2y;
        let ny = e1z * e2x - e1x * e2z;
        let nz = e1x * e2y - e1y * e2x;
        const nlen = Math.hypot(nx, ny, nz);
        if (nlen > 0) {
            nx /= nlen;
            ny /= nlen;
            nz /= nlen;
        }
        // In ECEF, outward = same direction as position vector from Earth center.
        // Flip normal if it points inward (dot with centroid < 0).
        const cx = (v0x + v1x + v2x) / 3;
        const cy = (v0y + v1y + v2y) / 3;
        const cz = (v0z + v1z + v2z) / 3;
        if (nx * cx + ny * cy + nz * cz < 0) {
            nx = -nx;
            ny = -ny;
            nz = -nz;
        }
        const ox = nx * FACE_LIFT_METERS;
        const oy = ny * FACE_LIFT_METERS;
        const oz = nz * FACE_LIFT_METERS;
        const faceVertices = new Float64Array([
            v0x + ox,
            v0y + oy,
            v0z + oz,
            v1x + ox,
            v1y + oy,
            v1z + oz,
            v2x + ox,
            v2y + oy,
            v2z + oz,
        ]);
        const geom = new Geometry({
            // @ts-expect-error Cesium typing mismatch - only position needed
            attributes: {
                position: new GeometryAttribute({
                    componentDatatype: ComponentDatatype.DOUBLE,
                    componentsPerAttribute: 3,
                    values: faceVertices,
                }),
            },
            vertexFormat: VertexFormat.POSITION_ONLY,
            indices: new Uint32Array([0, 1, 2]),
            primitiveType: PrimitiveType.TRIANGLES,
            boundingSphere: BoundingSphere.fromVertices(Array.from(faceVertices)),
        });
        GeometryPipeline.computeNormal(geom);
        const faceId = { faceIndex: f };
        faceIds[f] = faceId;
        instances[f] = new GeometryInstance({
            geometry: geom,
            modelMatrix: Matrix4.IDENTITY,
            attributes: {
                color: ColorGeometryInstanceAttribute.fromColor(Color.fromBytes(255, 255, 255, 38)),
            },
            id: faceId,
        });
    }
    const primitive = new Primitive({
        geometryInstances: instances,
        allowPicking: true,
        appearance: new PerInstanceColorAppearance({
            flat: false,
            translucent: true,
            faceForward: true,
        }),
        asynchronous: false,
    });
    return { primitive, faceIds };
}
/**
 * Update face colors on an existing per-face primitive.
 */
export function updateFaceColors(primitive, faceIds, selectedFaces, seedFaces, excludedFaces, hoveredFace) {
    for (let f = 0; f < faceIds.length; f++) {
        const attrs = primitive.getGeometryInstanceAttributes(faceIds[f]);
        if (!attrs)
            continue;
        let color;
        if (excludedFaces.has(f)) {
            color = COLOR_EXCLUDED;
        }
        else if (f === hoveredFace) {
            color = COLOR_HOVERED;
        }
        else if (seedFaces.has(f)) {
            color = COLOR_SEED;
        }
        else if (selectedFaces.has(f)) {
            color = COLOR_SELECTED;
        }
        else {
            color = COLOR_DEFAULT;
        }
        attrs.color = color.value;
    }
}
//# sourceMappingURL=mesh-face-primitive.js.map